import { has } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import Cookies from 'js-cookie';
import { shortenUUID } from 'ca-js-helper';
import { Media, MediaSet } from 'ca-api-client';
import Selected from '@/modules/selected';

const COOKIE_LIFETIME = 365;

const recurseChildren = (children) => {
    return children.data.map((child) => {
        if (has(child, 'children')) recurseChildren(child.children);
        return new MediaSet(child);
    });
};

const processJSON = (data) => {
    data.media.data = data.media.data.map((item) => new Media(item));
    if (has(data, 'children')) {
        data.children.data = recurseChildren(data.children);
    }
    if (!data.title) data.title = `Share ${shortenUUID(data.id)}`;
    return new MediaSet(data);
};

const getGuestToken = (shareID) => {
    return Cookies.get('app_guest_token', { path: `/s/${shareID}` });
};

const getGuestID = (shareID) => {
    let id = Cookies.get('app_guest_id', { path: `/s/${shareID}` });
    if (id === 'undefined') id = false;
    return id;
};

const getGuestEmail = (shareID) => {
    let email = Cookies.get('app_guest_email', { path: `/s/${shareID}` });
    if (email === 'undefined') email = false;
    return email;
};

const setGuestToken = (shareID, token) => {
    Cookies.set('app_guest_token', token, {
        secure: true,
        path: `/s/${shareID}`,
        sameSite: 'strict',
        expires: COOKIE_LIFETIME,
    });
};

const setGuestID = (shareID) => {
    Cookies.set('app_guest_id', uuidv4(), {
        secure: true,
        path: `/s/${shareID}`,
        sameSite: 'strict',
        expires: COOKIE_LIFETIME,
    });
};

const setGuestEmail = (shareID, email) => {
    Cookies.set('app_guest_email', email, {
        secure: true,
        path: `/s/${shareID}`,
        sameSite: 'strict',
        expires: COOKIE_LIFETIME,
    });
};

export const app = {
    namespaced: true,

    // module assets
    state: {}, // module state is already nested and not affected by namespace option
    getters: {
        isAdmin() {}, // -> getters['app/isAdmin']
        getDownloadManyUrl: (state) => (base64Items, payload) => {
            const url = new URL(`${ca.media.media_service.url}/media/download-many/${base64Items}`);
            const guestID = getGuestID(payload.id);
            const guestEmail = getGuestEmail(payload.id);

            if (guestID) url.searchParams.append('guest_id', guestID);
            if (guestEmail) url.searchParams.append('guest_email', guestEmail);

            return url.href;
        },
    },
    actions: {
        async login({ commit }, payload) {
            const url = `${process.env.MIX_MEDIA_SERVICE_URL}/shares/${payload.id}.json`;
            const params = { responseType: 'json', headers: {} };
            let guestEmail = getGuestEmail(payload.id);
            let guestID = getGuestID(payload.id);

            if (!guestEmail) {
                setGuestEmail(payload.id, payload.email);
                guestEmail = getGuestEmail(payload.id);
            }

            if (!guestID) {
                setGuestID(payload.id);
                guestID = getGuestID(payload.id);
            }

            if (payload.password) {
                params.headers['X-Guest-Token'] = payload.password;
            }

            if (guestEmail) {
                params.headers['X-Guest-Email'] = guestEmail;
            }

            if (guestID) {
                params.headers['X-Guest-ID'] = guestID;
            }

            const response = await axios.get(url, params);

            if (response.status === 200) {
                setGuestToken(payload.id, payload.password);
            }

            return processJSON(response.data.data);
        }, // -> dispatch('app/login')

        setData({ commit }, payload) {
            commit('share/item', payload);
            commit('setClient', payload.client, { root: true });
            commit('setProject', payload.project, { root: true });
            ca.client = { logo: payload.client.logo_url };
        }, // -> dispatch('app/setData')

        setClientProject({ commit }, payload) {
            commit('setClient', payload.client, { root: true });
            commit('setProject', payload.project, { root: true });
            ca.client = { logo: payload.client.logo_url };
        }, // -> dispatch('app/setClientProject')
    },

    // nested modules
    modules: {
        share: {
            namespaced: true,

            state: {
                item: {},
                sidebar: false,
                selected: new Selected(),
                guestEmail: null,
                guestID: null,
            },

            getters: {
                item(state) {
                    return state.item;
                },
                sidebar(state) {
                    return state.sidebar;
                },
            },

            mutations: {
                item(state, item) {
                    state.item = item;
                },
                sidebar(state, open) {
                    state.sidebar = open;
                },
            },

            actions: {
                async fetchJSON({ commit }, payload) {
                    const params = { responseType: 'json', headers: {} };
                    const guestToken = getGuestToken(payload.id);
                    let guestEmail = getGuestEmail(payload.id);
                    let guestID = getGuestID(payload.id);

                    console.log('Cookies', guestToken, guestEmail, guestID);

                    if (!guestEmail) {
                        setGuestEmail(payload.id, payload.email);
                        guestEmail = getGuestEmail(payload.id);
                    }

                    if (!guestID) {
                        setGuestID(payload.id);
                        guestID = getGuestID(payload.id);
                    }

                    if (guestToken) {
                        params.headers['X-Guest-Token'] = guestToken;
                    }

                    if (guestEmail) {
                        params.headers['X-Guest-Email'] = guestEmail;
                    }

                    if (guestID) {
                        params.headers['X-Guest-ID'] = guestID;
                    }

                    if (Object.keys(params.headers).length == 0) {
                        delete params.headers;
                    }

                    const set = await axios.get(
                        `${process.env.MIX_MEDIA_SERVICE_URL}/shares/${payload.id}.json`,
                        params
                    );
                    return processJSON(set.data.data);
                },
            },
        },
    },
};
